import { action, computed, observable } from 'mobx'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { type Task } from 'mobx-task'
import { required, validationContext } from 'validx'
import {
  type LocationOption,
  locationOptions,
  type PrimarySoftwareOption,
  primarySoftwareOptions,
  type SpecialtyOption,
  specialtyOptions,
  type SupplementalSoftwareOption,
  supplementalSoftwareOptions,
} from '@/features/admin/staffing/workspace-posting-field-options'
import {
  type WorkspacePosting,
  type WorkspacePostingJobType,
  type WorkspacePostingOpportunityType,
} from '@/features/admin/staffing/WorkspacePosting'
import { type WorkspacePostingDetails } from '@/features/admin/staffing/WorkspacePostingStore'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  mustHaveMaxLength,
  mustHaveMinLength,
  mustBeInRange,
} from '@/utils/validx-util'

/**
 * Base view model for the workspace posting dialog.
 */
export abstract class WorkspacePostingDialogViewModel {
  readonly modal = new ModalViewModel()

  /**
   * Location field options.
   */
  readonly locationItems: LocationOption[] = Object.values(locationOptions)

  /**
   * Primary software field options.
   */
  readonly primarySoftwareItems: PrimarySoftwareOption[] = Object.values(
    primarySoftwareOptions,
  )

  /**
   * Supplemental software field options.
   */
  readonly supplementalSoftwareItems: SupplementalSoftwareOption[] =
    Object.values(supplementalSoftwareOptions)

  /**
   * Specialties field options.
   */
  readonly specialtyItems: SpecialtyOption[] = Object.values(specialtyOptions)

  /**
   * Type of work field options.
   */
  readonly jobTypeItems: WorkspacePostingJobType[] = [
    'Prep',
    'Review',
    'Bookkeeping',
  ]

  /**
   * Current workspace posting, if relevant.
   */
  @observable accessor workspacePosting: WorkspacePosting | undefined =
    undefined

  /**
   * The workspace this posting belongs to.
   */
  @observable accessor workspace: Workspace | undefined = undefined

  /**
   * The opportunity types for this workspace
   */
  @observable accessor opportunityType: WorkspacePostingOpportunityType =
    'Unknown'

  /**
   * The location (state) relevant to this posting.
   */
  @observable accessor location: LocationOption | undefined = undefined

  /**
   * The primary software required for this posting.
   */
  @observable accessor primarySoftware: PrimarySoftwareOption | undefined =
    undefined

  /**
   * Whether a backup is required to be maintained for this posting.
   */
  @observable accessor backupRequired = false

  /**
   * Supplemental software used for this posting.
   */
  @observable accessor supplementalSoftware:
    SupplementalSoftwareOption | undefined = undefined

  /**
   * Pro specialties relevant for this posting.
   */
  @observable accessor specialties: SpecialtyOption[] = []

  /**
   * The number of pros required for this posting.
   * AKA capacity.
   */
  @observable accessor prosRequired = 0

  /**
   * The maximum number of workspaces a potential applicant for this posting can be part of.
   */
  @observable accessor maxConcurrentWorkspaces = 0

  /**
   * The type of work required for this posting.
   */
  @observable accessor typeOfWork: WorkspacePostingJobType[] = []

  /**
   * The description for the posting.
   */
  @observable accessor description = ''

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly _fieldLengths = {
    description: { min: 0, max: 1024 },
  } as const

  /**
   * Validation context for workspace postings.
   */
  readonly validation = validationContext(this, {
    workspace: [
      required('A workspace must be selected.'),
      (opts) =>
        opts.value !== Workspace.none || 'A workspace must be selected.',
    ],
    opportunityType: [required('Opportunity type is required')],
    description: [
      mustHaveMinLength(
        this._fieldLengths.description.min,
        `Description must not exceed ${this._fieldLengths.description.max} characters.`,
      ),
      mustHaveMaxLength(
        this._fieldLengths.description.max,
        `Description must not exceed ${this._fieldLengths.description.max} characters.`,
      ),
    ],
    prosRequired: [mustBeInRange(0, Infinity, 'Must be zero or greater.')],
    maxConcurrentWorkspaces: [
      mustBeInRange(0, Infinity, 'Must be zero or greater.'),
    ],
  })

  constructor(
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {}

  /**
   * Returns workspaces in which the user has the ADMIN_STAFFING_MANAGE permission
   * and staffing is enabled in the workspace config.
   */
  @computed
  get workspaces() {
    return this._getWorkspaceMemberships()
      .filter(
        (mb) =>
          mb.member.permissions.adminStaffingManage &&
          mb.workspace.config.staffing.enabled,
      )
      .map((mb) => {
        return mb.workspace
      })
  }

  /**
   * Whether the form can be submitted.
   */
  @computed
  get canSubmit() {
    return this.workspace !== Workspace.none && !this.submit.pending
  }

  /**
   * Returns whether the posting is for an Outsourcing workspace
   */
  @computed
  get isOutsourcing() {
    return this.opportunityType === 'Outsourcing'
  }

  /**
   * The details the form edits.
   */
  @computed
  get details(): WorkspacePostingDetails {
    return {
      location: this.location,
      primarySoftware: this.primarySoftware,
      backupFileNeeded: this.backupRequired,
      supplementalSoftware: this.supplementalSoftware,
      specialties: this.specialties,
      capacity: this.prosRequired,
      maxAllowedWorkspaces: this.maxConcurrentWorkspaces,
      jobTypes: this.typeOfWork,
      description: this.description,
    }
  }

  /**
   * Set workspace field.
   * @param workspace
   */
  @action.bound
  setWorkspace(workspace: Workspace | undefined) {
    this.workspace = workspace ?? Workspace.none
  }

  /**
   * Set Opportunity Type field
   * @param opportunityType
   */
  @action.bound
  setOpportunityType(
    opportunityType: WorkspacePostingOpportunityType | undefined,
  ) {
    this.opportunityType = opportunityType || 'Unknown'
  }

  /**
   * Set location field.
   * @param location
   */
  @action.bound
  setLocation(location: LocationOption | undefined) {
    this.location = location ?? undefined
  }

  /**
   * Set primary software field.
   * @param primarySoftware
   */
  @action.bound
  setPrimarySoftware(primarySoftware: PrimarySoftwareOption | undefined) {
    this.primarySoftware = primarySoftware ?? undefined
  }

  /**
   * Toggles the backup required field.
   */
  @action.bound
  toggleBackupRequired() {
    this.backupRequired = !this.backupRequired
  }

  /**
   * Set supplemental software field.
   * @param supplementalSoftware
   */
  @action.bound
  setSupplementalSoftware(
    supplementalSoftware: SupplementalSoftwareOption | undefined,
  ) {
    this.supplementalSoftware = supplementalSoftware ?? undefined
  }

  /**
   * Set specialties field.
   * @param specialties
   */
  @action.bound
  setSpecialties(specialties: SpecialtyOption[]) {
    this.specialties = specialties
  }

  /**
   * Toggle type of work stored in typeOfWork array.
   * @param typeOfWork
   */
  @action.bound
  toggleTypeOfWork(typeOfWork: WorkspacePostingJobType) {
    if (this.typeOfWork.includes(typeOfWork)) {
      const index = this.typeOfWork.findIndex((type) => type === typeOfWork)
      this.typeOfWork.splice(index, 1)
    } else {
      this.typeOfWork.push(typeOfWork)
    }
  }

  /**
   * Closes and resets workspace posting dialog.
   */
  @action.bound
  close() {
    this.modal.close()
    this.reset()
  }

  /**
   * Validates workspace posting dialog fields and returns whether validation passes.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears workspace posting dialog state.
   */
  @action.bound
  reset() {
    this.workspacePosting = undefined
    this.workspace = Workspace.none
    this.location = undefined
    this.primarySoftware = undefined
    this.backupRequired = false
    this.supplementalSoftware = undefined
    this.specialties = []
    this.prosRequired = 0
    this.maxConcurrentWorkspaces = 0
    this.typeOfWork = []
    this.description = ''
    this.opportunityType = 'Unknown'

    this.validation.reset()
  }

  abstract submit: Task<[], Promise<void>>
}
